import React, { createContext, useContext, useMemo } from 'react';

import {
  useStaffLevelStream,
  type UseStaffLevelStreamResult,
} from '@/hooks/useStaffLevelStream';

const StaffLevelStreamContext = createContext<UseStaffLevelStreamResult | null>(
  null
);

interface StaffLevelStreamProviderProps {
  children: React.ReactNode;
}

/**
 * Wraps the adaptive practice tree so the HomeView selector and the session
 * hook share one cohort selection. Mounted inside `AdaptivePracticePage` so
 * navigating between Home, TopicDetail, and the running session keeps the
 * staff "trying out as" pick stable for the whole tree.
 */
export const StaffLevelStreamProvider: React.FC<
  StaffLevelStreamProviderProps
> = ({ children }) => {
  const value = useStaffLevelStream();
  // Memoize so consumers that subscribe to a single field don't re-render on
  // every parent render — the underlying hook already returns stable refs for
  // its arrays/objects when nothing changes.
  const memoized = useMemo(
    () => value,
    [
      value.enabled,
      value.isLoading,
      value.options,
      value.selected,
      value.setSelected,
    ]
  );
  return (
    <StaffLevelStreamContext.Provider value={memoized}>
      {children}
    </StaffLevelStreamContext.Provider>
  );
};

/**
 * Access the staff "trying out as" state. When the provider is absent (e.g. a
 * test renders the selector in isolation), returns a disabled fallback so
 * consumers can render safely.
 */
export const useStaffLevelStreamContext = (): UseStaffLevelStreamResult => {
  const ctx = useContext(StaffLevelStreamContext);
  if (ctx) return ctx;
  return {
    enabled: false,
    isLoading: false,
    options: [],
    selected: null,
    setSelected: () => undefined,
  };
};
